import { useComputed, useSignal } from "@preact/signals";
import { html } from "htm/preact";
import { useState } from "preact/hooks";

import * as Bible from "../modules/bible";
import { dispatchEvent } from "../modules/events";
import { useChangeEffect } from "../modules/hooks";

export const BibleTranslationInfo = ({
  translation,
  variant,
}: {
  translation: Bible.TranslationPayload;
  variant: "button" | "default";
}) => {
  return html`
    <div
      class="BibleTranslation"
      data-variant=${variant}
      part="bible-translation"
    >
      ${variant === "button"
        ? html`<${TranslationButton} translation=${translation} />`
        : html`<${TranslationInfo} translation=${translation} />`}
    </div>
  `;
};

const TranslationInfo = ({
  translation,
}: {
  translation: Bible.TranslationPayload;
}) => {
  const copyright = translation.copyright;
  if (!copyright) /* c8 ignore next */ return null;
  return html`<div
    class="BibleTranslationInfo"
    id="translation-info"
    part="bible-translation-info"
  >
    <div class="BibleTranslationInfo__name" part="bible-translation-info-name">
      ${translation.full_name}
    </div>
    <p part="bible-translation-info-copyright">${copyright.citation}</p>
    <a
      href=${copyright.link}
      rel="noreferrer"
      target="_blank"
      part="bible-translation-info-copyright-link"
      >Learn More about ${translation.full_name}</a
    >
  </div>`;
};

const TranslationButton = ({
  translation,
}: {
  translation: Bible.TranslationPayload;
}) => {
  const isActive = useSignal(false);
  const isActiveHandler = (value: boolean) => () => {
    isActive.value = value;
  };
  const className = useComputed(
    () =>
      `BibleTranslationButton ${
        isActive.value ? `BibleTranslationButton--active` : ""
      }`,
  );
  const [isExpanded, setIsExpanded] = useState(false);
  useChangeEffect(() => {
    dispatchEvent("translationtoggled", {
      expanded: isExpanded,
      translation,
    });
  }, [isExpanded]);
  return html`
    <button
      aria-label=${`More information about the ${translation.full_name}`}
      aria-expanded=${isExpanded ? "true" : "false"}
      aria-controls="translation-info"
      class=${className}
      data-testid="button"
      id="translation-info-button"
      onclick=${() => {
        setIsExpanded(!isExpanded);
      }}
      onTouchCancel=${isActiveHandler(false)}
      onTouchEnd=${isActiveHandler(false)}
      onTouchStart=${isActiveHandler(true)}
    >
      <span class="BibleTranslationButton_container"
        ><span>${translation.name}</span>
        <svg
          viewBox="0 0 25 24"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.5 0C5.84375 0 0.5 5.39062 0.5 12C0.5 18.6562 5.84375 24 12.5 24C19.1094 24 24.5 18.6562 24.5 12C24.5 5.39062 19.1094 0 12.5 0ZM12.5 21.75C7.10938 21.75 2.75 17.3906 2.75 12C2.75 6.65625 7.10938 2.25 12.5 2.25C17.8438 2.25 22.25 6.65625 22.25 12C22.25 17.3906 17.8438 21.75 12.5 21.75ZM14.375 15.75H13.625V11.625C13.625 11.0156 13.1094 10.5 12.5 10.5H11C10.3438 10.5 9.875 11.0156 9.875 11.625C9.875 12.2812 10.3438 12.75 11 12.75H11.375V15.75H10.625C9.96875 15.75 9.5 16.2656 9.5 16.875C9.5 17.5312 9.96875 18 10.625 18H14.375C14.9844 18 15.5 17.5312 15.5 16.875C15.5 16.2656 14.9844 15.75 14.375 15.75ZM12.5 9C13.2969 9 14 8.34375 14 7.5C14 6.70312 13.2969 6 12.5 6C11.6562 6 11 6.70312 11 7.5C11 8.34375 11.6562 9 12.5 9Z"
            fill="currentColor"
          />
        </svg>
      </span>
    </button>
    <div aria-describedby="translation-info-button" hidden=${!isExpanded}>
      <${TranslationInfo} translation=${translation} />
    </div>
  `;
};
